/* iter 241 Lot 4 — Engine-aware SEO panel.
 *
 * Sits at the top of AnalyzeStep. Only visible when Multimoteur V2 is
 * enabled AND the active engine is different from the primary engine
 * (for the primary engine we keep the legacy Analyze UX untouched to
 * avoid regressing existing users).
 *
 *  - Vierge (no engine-scoped data)  → CTA "Lancer l'analyse"
 *  - Analysé (has themes, not validated) → banner + "Valider"
 *  - Configuré (has themes, validated)   → summary
 *
 *  Reads/writes via /api/v2/projects/{pid}/engines/{eid}/seo. */
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { useActiveEngine } from '@/context/EngineContext';
import { Loader2, Sparkles, CheckCircle, PlayCircle, RefreshCcw, ShieldCheck } from 'lucide-react';

export default function EngineSeoPanel({ projectId, onAfterAnalyze }) {
  const { activeEngine, activeEngineId, multiEnabled } = useActiveEngine();
  const [state, setState] = useState({ loading: true, data: null });
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    if (!multiEnabled || !activeEngineId) {
      setState({ loading: false, data: null });
      return;
    }
    setState({ loading: true, data: null });
    try {
      const { data } = await apiClient.get(
        `/v2/projects/${projectId}/engines/${activeEngineId}/seo`
      );
      setState({ loading: false, data });
    } catch (e) {
      setState({ loading: false, data: null, error: e });
    }
  }, [projectId, activeEngineId, multiEnabled]);

  useEffect(() => { refresh(); }, [refresh]);

  const launchEngineAnalysis = async () => {
    setRunning(true);
    try {
      // 1. Trigger the legacy project-scope analyzer (source of truth
      //    for the AI extraction). The route is expected to already
      //    exist and unchanged.
      const { data: legacy } = await apiClient.post(`/projects/${projectId}/analyze`);
      // 2. Snapshot the result into the engine-scoped SEO record.
      const themes = legacy.themes || legacy.extracted_themes || {};
      const keywords = legacy.keywords || themes.keywords || [];
      await apiClient.put(
        `/v2/projects/${projectId}/engines/${activeEngineId}/seo`,
        {
          analysis_result: legacy,
          extracted_themes: { ...themes, keywords, validated: false },
          keywords,
          seo_score: legacy.seo_score || null,
          validated: false,
        }
      );
      toast.success(`Analyse enregistrée pour « ${activeEngine.name} »`);
      await refresh();
      if (onAfterAnalyze) onAfterAnalyze();
    } catch (e) {
      toast.error("Analyse impossible : " + (e?.response?.data?.detail || e?.message || 'erreur'));
    } finally {
      setRunning(false);
    }
  };

  const validate = async () => {
    if (!state.data) return;
    setRunning(true);
    try {
      await apiClient.put(
        `/v2/projects/${projectId}/engines/${activeEngineId}/seo`,
        {
          analysis_result: state.data.analysis_result || {},
          extracted_themes: { ...(state.data.extracted_themes || {}), validated: true },
          keywords: (state.data.extracted_themes || {}).keywords || [],
          validated: true,
        }
      );
      toast.success('Thèmes validés pour ce moteur');
      await refresh();
    } catch (e) {
      toast.error("Validation impossible");
    } finally {
      setRunning(false);
    }
  };

  if (!multiEnabled || !activeEngine) return null;

  // On the primary engine, we let the legacy Analyze UX handle
  // everything (mirrored via PUT for the engine-scope). We only expose
  // a compact status row.
  const isPrimary = activeEngine.is_primary;
  const analyzed = state.data && state.data.status === 'analyzed';
  const validated = analyzed && state.data.validated;
  const themes = (state.data?.extracted_themes) || {};
  const keywords = themes.keywords || [];

  return (
    <div
      data-testid="engine-seo-panel"
      data-engine-id={activeEngineId}
      data-engine-status={state.loading ? 'loading' : (validated ? 'validated' : (analyzed ? 'analyzed' : 'not_started'))}
      className="mb-6 p-4 rounded-xl"
      style={{
        background: 'hsl(var(--color-surface-elevated))',
        border: '1px solid hsl(var(--color-border))',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="w-4 h-4" style={{ color: 'hsl(var(--color-primary))' }} />
            <span>Analyse SEO du moteur actif</span>
            {isPrimary && (
              <span className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ background: 'hsl(var(--color-primary) / 0.12)',
                              color: 'hsl(var(--color-primary))' }}>
                moteur principal
              </span>
            )}
          </div>
          {state.loading ? (
            <div className="mt-2 text-xs text-semantic-muted flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
            </div>
          ) : validated ? (
            <div className="mt-1 text-xs text-semantic-muted"
                 data-testid="engine-seo-status-validated">
              <CheckCircle className="w-3 h-3 inline mr-1" />
              Configuré — {keywords.length} mots-clés validés
            </div>
          ) : analyzed ? (
            <div className="mt-1 text-xs text-semantic-muted"
                 data-testid="engine-seo-status-analyzed">
              Analysé — {keywords.length} mots-clés détectés (à valider)
            </div>
          ) : (
            <div className="mt-1 text-xs text-semantic-muted"
                 data-testid="engine-seo-status-empty">
              Analyse SEO à lancer pour ce moteur
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!analyzed && !state.loading && (
            <button
              data-testid="engine-seo-launch"
              onClick={launchEngineAnalysis}
              disabled={running}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg"
              style={{ background: 'hsl(var(--color-primary))', color: 'white' }}
            >
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
              Lancer l&apos;analyse
            </button>
          )}
          {analyzed && !validated && (
            <button
              data-testid="engine-seo-validate"
              onClick={validate}
              disabled={running}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg"
              style={{ background: 'hsl(var(--color-success))', color: 'white' }}
            >
              {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              Valider les thèmes
            </button>
          )}
          {analyzed && (
            <button
              data-testid="engine-seo-refresh"
              onClick={launchEngineAnalysis}
              disabled={running}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg"
              style={{ background: 'transparent',
                        border: '1px solid hsl(var(--color-border))' }}
            >
              <RefreshCcw className="w-3 h-3" /> Relancer
            </button>
          )}
        </div>
      </div>

      {analyzed && keywords.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5"
             data-testid="engine-seo-keywords">
          {keywords.slice(0, 12).map((k) => (
            <span key={k} className="text-[11px] px-2 py-0.5 rounded"
                  style={{ background: 'hsl(var(--color-primary) / 0.1)',
                            color: 'hsl(var(--color-primary))' }}>
              {k}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
